import uniqBy from 'lodash/uniqBy'

import type { NativeTag, NativeTagWithCategory } from 'types/track'

export const getCategoriesAndTags = (
  filteredTags: NativeTagWithCategory[],
  value?: number[],
): { [key: string]: NativeTag[] } => {
  const tagGroups: { [key: string]: NativeTag[] } = {}
  const categories = uniqBy(
    filteredTags.map(tag => tag.category),
    'id',
  )
  categories.forEach(category => {
    const tags =
      filteredTags
        .filter(tag => tag.category_id === category.id)
        .filter(tag => !value?.includes(tag.id)) || []

    if (tags.length > 0) {
      tagGroups[category.name] = tags
    }
  })
  return tagGroups
}

export const getMatchingTagCategories = (
  searchValue: string,
  filteredTags: NativeTagWithCategory[],
) => {
  if (!searchValue || !filteredTags?.length) return []

  return uniqBy(
    filteredTags
      .filter(tag => tag.name.toLowerCase().includes(searchValue))
      .map(tag => tag.category.name),
    name => name,
  )
}

// Both lists narrow the same way, so they normalise the search term the same.
const matchingName = <T extends { name: string }>(
  tags: T[],
  searchValue: string,
): T[] => {
  const search = searchValue.toLowerCase().trim()
  if (!search) return tags
  return tags.filter(tag => tag.name.toLowerCase().includes(search))
}

/**
 * One entry per selected value (native tags by id, playlist tags by name).
 * Deduped by key: records sharing a name are one selection.
 */
export const getFilteredSelectedTags = <
  T extends { name: string },
  K extends number | string,
>(
  value: K[] | undefined,
  searchValue: string,
  allTags: T[],
  getKey: (tag: T) => K,
): T[] => {
  if (!value) return []
  const selected = new Set<K>(value)
  const selectedTags = uniqBy(
    allTags.filter(tag => selected.has(getKey(tag))),
    getKey,
  )
  return matchingName(selectedTags, searchValue)
}

/**
 * Counterpart to getFilteredSelectedTags for the flat (no-category) list:
 * everything not currently selected, narrowed by `searchValue`.
 */
export const getFilteredUnselectedTags = <
  T extends { name: string },
  K extends number | string,
>(
  value: K[] | undefined,
  searchValue: string,
  allTags: T[],
  getKey: (tag: T) => K,
): T[] => {
  const selected = new Set<K>(value ?? [])
  return matchingName(
    allTags.filter(tag => !selected.has(getKey(tag))),
    searchValue,
  )
}
